import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import ColorPicker from './ColorPicker';

const InputDialog = ({ open, onClose, onInsert, initialData }) => {
  const defaultData = {
    label: '',
    placeholder: 'Enter text...',
    defaultValue: '',
    name: '',
    type: 'text',
    size: 'medium',
    alignment: 'left',
    width: '300px',
    fullWidth: false,
    required: false,
    backgroundColor: '#FFFFFF',
    textColor: '#333333',
    borderColor: '#CCCCCC',
    focusBorderColor: '#0F3A61',
    borderRadius: '4',
    cssClass: '',
    pattern: '',
    min: '',
    max: '',
    step: '1',
  };
  
  const [inputData, setInputData] = useState(defaultData);
  
  // Update form when editing existing input
  React.useEffect(() => {
    if (initialData) {
      setInputData({ ...defaultData, ...initialData });
    } else {
      setInputData(defaultData);
    }
  }, [initialData, open]);

  const handleChange = (field, value) => {
    setInputData(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (type, color) => {
    if (type === 'background') {
      handleChange('backgroundColor', color);
    } else if (type === 'color') {
      handleChange('textColor', color);
    } else if (type === 'border') {
      handleChange('borderColor', color);
    } else if (type === 'focusBorder') {
      handleChange('focusBorderColor', color);
    }
  };

  const handleInsert = () => {
    // Clean up empty values for number fields
    const cleanedData = { ...inputData };
    if (inputData.type === 'number') {
      if (!inputData.min) delete cleanedData.min;
      if (!inputData.max) delete cleanedData.max;
      if (!inputData.step) cleanedData.step = '1';
    }
    
    onInsert(cleanedData);
    onClose();
  };

  // Common input types for forms
  const inputTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'tel', label: 'Phone' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'url', label: 'URL' },
  ];

  // Common validation patterns
  const commonPatterns = [
    { label: 'None', value: '' },
    { label: 'Email', value: '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$' },
    { label: 'Phone (US)', value: '\\d{3}-\\d{3}-\\d{4}' },
    { label: 'Alphanumeric', value: '[a-zA-Z0-9]+' },
    { label: 'Letters only', value: '[a-zA-Z]+' },
    { label: 'Numbers only', value: '[0-9]+' },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {initialData ? 'Edit Input Field' : 'Insert Input Field'}
        <IconButton
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Label */}
          <TextField
            fullWidth
            label="Field Label"
            value={inputData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., Full Name"
            helperText="Optional label shown above the input"
          />

          {/* Placeholder */}
          <TextField
            fullWidth
            label="Placeholder Text"
            value={inputData.placeholder}
            onChange={(e) => handleChange('placeholder', e.target.value)}
            placeholder="e.g., Enter your name..."
          />

          {/* Default Value */}
          <TextField
            fullWidth
            label="Default Value"
            value={inputData.defaultValue}
            onChange={(e) => handleChange('defaultValue', e.target.value)}
            helperText="Pre-filled value (will show in PDF)"
          />

          {/* Field Name */}
          <TextField
            fullWidth
            label="Field Name"
            value={inputData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., user_name"
            helperText="Name attribute for form submission"
          />

          {/* Type and Size Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Input Type</InputLabel>
              <Select
                value={inputData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                label="Input Type"
              >
                {inputTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Size</InputLabel>
              <Select
                value={inputData.size}
                onChange={(e) => handleChange('size', e.target.value)}
                label="Size"
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Width and Required */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              sx={{ flex: 1 }}
              label="Width"
              value={inputData.width}
              onChange={(e) => handleChange('width', e.target.value)}
              disabled={inputData.fullWidth}
              placeholder="e.g., 300px"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={inputData.fullWidth}
                  onChange={(e) => handleChange('fullWidth', e.target.checked)}
                />
              }
              label="Full Width"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={inputData.required}
                  onChange={(e) => handleChange('required', e.target.checked)}
                />
              }
              label="Required"
            />
          </Box>

          {/* Number-specific fields */}
          {inputData.type === 'number' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                sx={{ flex: 1 }}
                label="Min Value"
                value={inputData.min}
                onChange={(e) => handleChange('min', e.target.value)}
                type="number"
              />
              <TextField
                sx={{ flex: 1 }}
                label="Max Value"
                value={inputData.max}
                onChange={(e) => handleChange('max', e.target.value)}
                type="number"
              />
              <TextField
                sx={{ flex: 1 }}
                label="Step"
                value={inputData.step}
                onChange={(e) => handleChange('step', e.target.value)}
                type="number"
              />
            </Box>
          )}

          {/* Alignment */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Field Alignment
            </Typography>
            <ToggleButtonGroup
              value={inputData.alignment}
              exclusive
              onChange={(e, value) => value && handleChange('alignment', value)}
              fullWidth
            >
              <ToggleButton value="left">
                <FormatAlignLeft />
              </ToggleButton>
              <ToggleButton value="center">
                <FormatAlignCenter />
              </ToggleButton>
              <ToggleButton value="right">
                <FormatAlignRight />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Colors */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
              Colors
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Background</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ColorPicker
                    icon={<Box sx={{ width: 24, height: 24, bgcolor: inputData.backgroundColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                    tooltip="Background Color"
                    value={inputData.backgroundColor}
                    onChange={handleColorChange}
                    type="background"
                  />
                  <TextField
                    size="small"
                    value={inputData.backgroundColor}
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Text</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ColorPicker
                    icon={<Box sx={{ width: 24, height: 24, bgcolor: inputData.textColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                    tooltip="Text Color"
                    value={inputData.textColor}
                    onChange={handleColorChange}
                    type="color"
                  />
                  <TextField
                    size="small"
                    value={inputData.textColor}
                    onChange={(e) => handleChange('textColor', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Border</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ColorPicker
                    icon={<Box sx={{ width: 24, height: 24, bgcolor: inputData.borderColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                    tooltip="Border Color"
                    value={inputData.borderColor}
                    onChange={handleColorChange}
                    type="border"
                  />
                  <TextField
                    size="small"
                    value={inputData.borderColor}
                    onChange={(e) => handleChange('borderColor', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Focus Border</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ColorPicker
                    icon={<Box sx={{ width: 24, height: 24, bgcolor: inputData.focusBorderColor, border: 1, borderColor: 'divider', borderRadius: 1 }} />}
                    tooltip="Focus Border Color"
                    value={inputData.focusBorderColor}
                    onChange={handleColorChange}
                    type="focusBorder"
                  />
                  <TextField
                    size="small"
                    value={inputData.focusBorderColor}
                    onChange={(e) => handleChange('focusBorderColor', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Border Radius and CSS Class */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              sx={{ flex: 1 }}
              label="Border Radius"
              value={inputData.borderRadius}
              onChange={(e) => handleChange('borderRadius', e.target.value)}
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">px</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 50 }}
            />
            
            <TextField
              sx={{ flex: 1 }}
              label="CSS Class"
              value={inputData.cssClass}
              onChange={(e) => handleChange('cssClass', e.target.value)}
              placeholder="form-input custom-field"
              helperText="Space-separated class names"
            />
          </Box>

          {/* Validation Pattern */}
          <FormControl fullWidth>
            <InputLabel>Validation Pattern</InputLabel>
            <Select
              value={inputData.pattern}
              onChange={(e) => handleChange('pattern', e.target.value)}
              label="Validation Pattern"
            >
              {commonPatterns.map(pattern => (
                <MenuItem key={pattern.label} value={pattern.value}>
                  {pattern.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preview */}
          <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            {inputData.cssClass && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                CSS Classes: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: 2 }}>{inputData.cssClass}</code>
              </Typography>
            )}
            <Box sx={{ textAlign: inputData.alignment, mt: 2 }}>
              <div style={{ display: 'inline-block', width: inputData.fullWidth ? '100%' : 'auto' }}>
                {inputData.label && (
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#333' }}>
                    {inputData.label}
                    {inputData.required && <span style={{ color: 'red' }}> *</span>}
                  </label>
                )}
                <input
                  type={inputData.type}
                  placeholder={inputData.placeholder}
                  defaultValue={inputData.defaultValue}
                  style={{
                    width: inputData.fullWidth ? '100%' : inputData.width,
                    padding: inputData.size === 'small' ? '4px 8px' : inputData.size === 'large' ? '12px 16px' : '8px 12px',
                    fontSize: inputData.size === 'small' ? '14px' : inputData.size === 'large' ? '18px' : '16px',
                    borderRadius: `${inputData.borderRadius}px`,
                    border: `1px solid ${inputData.borderColor}`,
                    backgroundColor: inputData.backgroundColor,
                    color: inputData.textColor,
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleInsert} 
          variant="contained"
        >
          {initialData ? 'Update Input' : 'Insert Input'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InputDialog;